import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, inject, type OnInit } from '@angular/core';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import { LocaleSharedModule } from '@app/i18n/locale-shared.module';
import { LibraryElementsModule } from '@app/library/library-elements.module';
import { TeacherCardModule } from '@app/teachers/teacher-card/teacher-card.module';
import { HorizontalScrollMenuModule } from '@core/components/horizontal-scroll-menu/horizontal-scroll-menu.module';
import { SpinnersModule } from '@core/components/spinners/spinners.module';
import { CoreModule } from '@core/core.module';
import { vimeoEmbedUrl, youtubeEmbedUrl } from '@core/helpers/video';
import { PipesModule } from '@core/pipes/pipes.module';
import { ConfigService, ProfileService } from '@core/services';
import { Store } from '@ngrx/store';
import { ITeachingSubject, Language, Subject, User, UserProfile } from 'lingo2-models';
import { Observable } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { ILibraryTitle } from '../../library-title/library-title.component';
import { AbstractPane } from '../models/abstract-pane';

interface ITeachingSubjectItem {
  value: string | number;
  title: string;
  subject: ITeachingSubject;
}

// TODO Инициализация данных очень сбойная, переписать!
@Component({
  selector: 'app-library-teacher-card',
  templateUrl: './library-teacher-card.component.html',
  styleUrls: ['./library-teacher-card.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  imports: [
    LocaleSharedModule,
    CommonModule,
    CoreModule,
    SpinnersModule,
    LibraryElementsModule,

    HorizontalScrollMenuModule,
    PipesModule,
    TeacherCardModule,
  ],
})
export class LibraryTeacherCardComponent extends AbstractPane implements OnInit {
  private profileService = inject(ProfileService);
  private configService = inject(ConfigService);
  private sanitizer = inject(DomSanitizer);
  private store = inject(Store);

  public isMediaPrepared = false;
  public teachingSubjectsList: ITeachingSubjectItem[] = [];
  public teachingSubjectIndex = -1;
  public teachingSubjectMore = false;
  public me: User;
  public blockTitle: ILibraryTitle = {
    title: 'library.titles.teacher-of-the-day',
  };

  public safeMediaUrl: SafeResourceUrl;
  public canSignup$: Observable<boolean>;
  public show: boolean;

  private _profile: UserProfile;
  private _subjects: Subject[] = [];
  private _languages: Language[] = [];

  public ngOnInit(): void {
    this.configService.subjects$.pipe(takeUntil(this.destroyed$)).subscribe({
      next: (subjects) => {
        this._subjects = subjects;
      },
      error: (error) => {
        this.errorNotificationService.captureError(error, 'LOAD-SOMEDATA');
      },
    });
    this.configService.languages$.pipe(takeUntil(this.destroyed$)).subscribe({
      next: (languages) => {
        this._languages = languages;
      },
      error: (error) => {
        this.errorNotificationService.captureError(error, 'LOAD-SOMEDATA');
      },
    });
    this.configInit();
  }

  private configInit() {
    if (!this.user) {
      return;
    }
    this.profileService
      .getProfile(this.user.id)
      .pipe(takeUntil(this.destroyed$))
      .subscribe({
        next: (profile) => {
          this.show = true;
          this._profile = profile;
          this.user.profile = this._profile;
          this.detectChanges();

          this.teachingSubjectsList = (this._profile.teaching_subjects || [])
            .filter((s) => s.subject_id > 0)
            .map((subject, index) => {
              const _subject = this._subjects.find((s) => s.id === +subject.subject_id);
              return {
                value: index,
                title: (_subject ? _subject.title : null) || '---',
                subject,
              };
            });

          if (this.teachingSubjectsList.length > 0 && !this.safeMediaUrl) {
            this.teachingSubjectIndex = 0;
            this.prepareMedia();
          }

          this.detectChanges();
        },
        error: (error) => {
          this.errorNotificationService.captureError(error, 'LOAD-SOMEDATA');
        },
      });
  }

  protected onContentLoaded(viaQuery?: boolean): void {
    this.isVisible = !!this._pane?.user;
  }

  public onTeachingSubjectChange(index: number) {
    this.teachingSubjectIndex = index;
    this.prepareMedia();
  }

  public get teachingSubject(): ITeachingSubject {
    if (this.teachingSubjectIndex >= 0 && this.teachingSubjectsList[this.teachingSubjectIndex]) {
      return this.teachingSubjectsList[this.teachingSubjectIndex].subject;
    }
  }

  public get descriptionTeachingSubject() {
    if (this.teachingSubjectMore || this.descriptionTeachSubLen < 400) {
      return this.teachingSubject.description;
    }
    return this.teachingSubject.description.substr(0, 400) + ' ...';
  }

  public get descriptionTeachSubLen() {
    return this.teachingSubject.description.length || 0;
  }

  public prepareMedia() {
    const teachingSubject = this.teachingSubject;
    if (!teachingSubject || !teachingSubject.video_url) {
      this.safeMediaUrl = null;
      this.isMediaPrepared = false;
      return;
    }
    const embedUrl = youtubeEmbedUrl(teachingSubject.video_url) || vimeoEmbedUrl(teachingSubject.video_url);
    if (embedUrl) {
      this.safeMediaUrl = this.sanitizer.bypassSecurityTrustResourceUrl(embedUrl);
      this.isMediaPrepared = true;
    } else {
      this.safeMediaUrl = null;
      this.isMediaPrepared = false;
    }
  }
}
